// shared-components/context/CartContext.types.ts

import { createContext } from "react";
import type { DigitalMetadata } from "../types/DigitalProductTypes";

export interface CartItem {
  id: string;
  productId: string;
  name: string;
  price: number;
  quantity: number;
  maxQuantity?: number;
  image?: string;
  variant?: string;
  // Digital product fields
  isDigital?: boolean;
  digitalMetadata?: DigitalMetadata;
  // Preview session ID for conversion tracking
  previewSessionId?: string;
}

export interface ShippingAddress {
  name: string;
  email: string;
  address: string;
  city: string;
  state?: string;
  postalCode: string;
  country: string;
}

export interface ShippingOption {
  id: string;
  name: string;
  price: number;
  estimatedDays?: number;
}

interface UndoOperation {
  type: string;
  timestamp: number;
  previousState: Partial<CartState>;
}

export interface CartState {
  items: CartItem[];
  lastUpdated: number;
  sessionId: string;
  isLoading: boolean;
  error: Error | null;
  lastAction: string | null;
  undoStack: UndoOperation[];
  shippingAddress: ShippingAddress | null;
  shippingOption: ShippingOption | null;
  paymentStatus: string | null;
  paymentError: string | null;
}

export interface CartActions {
  addItem: (item: Omit<CartItem, "id">) => void;
  removeItem: (itemId: string) => void;
  updateQuantity: (itemId: string, quantity: number) => void;
  clearCart: () => void;
  bulkAdd: (items: Omit<CartItem, "id">[]) => void;
  bulkRemove: (itemIds: string[]) => void;
  setShippingAddress: (address: ShippingAddress) => void;
  setShippingOption: (option: ShippingOption) => void;
  setPaymentStatus: (status: string) => void;
  setPaymentError: (error: string) => void;
}

export interface CartComputedProperties {
  // Digital/Physical item separation
  digitalItems: CartItem[];
  physicalItems: CartItem[];
  hasDigitalItems: boolean;
  hasPhysicalItems: boolean;
  // Enhanced totals
  totalItems: number;
  totalPrice: number;
}

export type CartContextValue = CartState & CartActions & CartComputedProperties;

export const CartContext = createContext<CartContextValue | null>(null);
